import { Space, Flex, Button, Divider, Row, Col, Tooltip, Tag } from "antd"
import { ExclamationCircleFilled } from "@ant-design/icons"
import { Text, Title } from "ui/Typography"
import styles from "./index.module.css"
import React, { useState } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { useTranslation } from "react-i18next"
import RefundModal from "./Modals/Refund"
import {
  BOOKING_REQUEST_TYPES,
  PAYMENT_STATUS,
  PAYMENT_HISTORY_STATUS,
  PAYMENT_HISTORY_TYPES,
  PAYMENT_HISTORY_ACTIONS
} from "../../utils"
import { colors } from "themes/variables"
import { useDispatch } from "react-redux"
import { getRefundDetails } from "store/bookings"
import { numberToCurrency } from "helpers/string"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const PaymentHistory = ({ paymentHistory, bookingStatus, bookingUUID, remainingBalance, paymentStatus }) => {
  const { t } = useTranslation()
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false)
  const dispatch = useDispatch()
  const { hasPermission } = usePermissions()

  const canEditBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.EDIT)

  const onOpen = async () => {
    setIsRefundModalOpen(true)
    await dispatch(getRefundDetails(bookingUUID))
  }

  const onClose = () => {
    setIsRefundModalOpen(false)
  }
  const bookingStatusLowerCase = bookingStatus?.toLowerCase()
  const paymentStatusLowerCase = paymentStatus?.toLowerCase()
  const isPartialPayment = paymentStatusLowerCase === PAYMENT_STATUS.PARTIAL || paymentStatusLowerCase === PAYMENT_STATUS.PARTIAL_REFUND
  const isRefundEnable =
    canEditBooking &&
    (bookingStatusLowerCase === BOOKING_REQUEST_TYPES.CANCELED ||
      bookingStatusLowerCase === BOOKING_REQUEST_TYPES.COMPLETED ||
      bookingStatusLowerCase === BOOKING_REQUEST_TYPES.UPCOMING) &&
    (isPartialPayment || Number(remainingBalance) === 0) &&
    paymentStatusLowerCase !== PAYMENT_STATUS.REFUNDED

  const transactions = paymentHistory?.filter((history) => !history?.action) || []
  const refundAttempt = paymentHistory?.find((history) => history?.action === PAYMENT_HISTORY_ACTIONS.REFUND_ATTEMPT)
  const hasFailedRefundAttempts = Number(refundAttempt?.failure_count) > 0

  return (
    <>
      <Space direction="vertical" size={8} className={`${styles.paymentHistoryContainer} w-full p-xl`}>
        <Flex align="center" justify="space-between">
          <Title level={4}>{t("client.client_booking.payment_history")}</Title>
          {isRefundEnable && (
            <Button type="link" className="p-0" onClick={onOpen}>
              {t("client.client_booking.refund")}
            </Button>
          )}
        </Flex>
        <Flex vertical style={{ maxHeight: "180px", overflowY: "auto", scrollbarWidth: "none" }}>
          {transactions.map((history, idx) => {
            const {
              type,
              paid_on,
              method,
              last4,
              amount,
              crew_names,
              note,
              payer_name,
              status,
              failed_at,
              requested_on,
              failure_reason
            } = history || {}
            const crewList = crew_names?.split(",").map((name) => name.trim()).filter(Boolean) || []
            const visibleCrew = crewList.slice(0, 2)
            const hiddenCrew = crewList.slice(2)
            const isRefund = type?.toLowerCase() === PAYMENT_HISTORY_TYPES.REFUND
            const statusLowerCase = status?.toLowerCase()
            const isFailed = statusLowerCase === PAYMENT_HISTORY_STATUS.FAILED
            const isPending = statusLowerCase === PAYMENT_HISTORY_STATUS.PENDING
            let amountColor = colors.textPrimary
            if (isFailed) amountColor = colors.textSecondary
            else if (isRefund) amountColor = colors.error
            return (
              <React.Fragment key={paid_on || failed_at || idx}>
                <Row>
                  <Col xs={14}>
                    <Flex vertical>
                      <Flex align="center" gap={8} wrap="wrap">
                        <Text>
                          {type || ""} ({method || ""}
                          {last4 && ` **** ${last4}`})
                        </Text>
                        {isFailed && <Tag color="error">{t("client.client_booking.payment_failed")}</Tag>}
                        {isPending && <Tag color="warning">{t("client.client_booking.payment_pending")}</Tag>}
                      </Flex>
                      <Text color={amountColor} className={isFailed ? styles.failedAmount : ""}>
                        {amount ? numberToCurrency(amount) : ""}
                      </Text>
                      {isFailed && failure_reason && (
                        <Tooltip title={failure_reason}>
                          <Text color={colors.error} className={styles.noteText}>
                            {t("client.client_booking.failure_reason", { reason: failure_reason })}
                          </Text>
                        </Tooltip>
                      )}
                      {payer_name && (
                        <Text color={colors.textSecondary}>Paid by {payer_name}</Text>
                      )}
                      {crewList.length > 0 && (
                        <Flex align="center" wrap="wrap">
                          {visibleCrew.map((name) => (
                            <Tag key={name} color="processing">
                              {name}
                            </Tag>
                          ))}
                          {hiddenCrew.length > 0 && (
                            <Tooltip title={hiddenCrew.join(", ")}>
                              <Tag color="processing" style={{ cursor: "pointer" }}>
                                +{hiddenCrew.length}
                              </Tag>
                            </Tooltip>
                          )}
                        </Flex>
                      )}
                      {note && (
                        <Tooltip title={note}>
                          <Text color={colors.textSecondary} className={styles.noteText}>
                            Note: {note}
                          </Text>
                        </Tooltip>
                      )}
                    </Flex>
                  </Col>
                  <Col xs={10}>
                    {paid_on && (
                      <Flex vertical>
                        <Text>{isRefund ? t("client.client_booking.refunded_on") : t("client.client_booking.paid_on")}</Text>
                        <Text>{moment(paid_on).format("MM/DD/YYYY h:mm A")}</Text>
                      </Flex>
                    )}
                    {!paid_on && failed_at && (
                      <Flex vertical>
                        <Text color={colors.error}>{t("client.client_booking.failed_on")}</Text>
                        <Text color={colors.error}>{moment(failed_at).format("MM/DD/YYYY h:mm A")}</Text>
                      </Flex>
                    )}
                    {!paid_on && !failed_at && requested_on && (
                      <Flex vertical>
                        <Text>{t("client.client_booking.requested_on")}</Text>
                        <Text>{moment(requested_on).format("MM/DD/YYYY")}</Text>
                      </Flex>
                    )}
                  </Col>
                </Row>
                <Divider size="small" />
              </React.Fragment>
            )
          })}
        </Flex>
        {hasFailedRefundAttempts && (
          <Flex align="center" gap={8} className={`${styles.refundAttemptBanner} p-s`}>
            <ExclamationCircleFilled style={{ color: colors.error }} />
            <Text color={colors.error}>
              {t("client.client_booking.refund_attempts_summary", {
                failureCount: refundAttempt.failure_count,
                amount: numberToCurrency(refundAttempt.attempted_amount)
              })}
            </Text>
          </Flex>
        )}
      </Space>
      {isRefundModalOpen && <RefundModal onOpen={isRefundModalOpen} onClose={onClose} bookingUUID={bookingUUID} />}
    </>
  )
}

PaymentHistory.propTypes = {
  paymentHistory: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      paid_on: PropTypes.string,
      type: PropTypes.string,
      method: PropTypes.string,
      last4: PropTypes.string,
      status: PropTypes.string,
      failed_at: PropTypes.string,
      requested_on: PropTypes.string,
      failure_reason: PropTypes.string,
      failure_code: PropTypes.string,
      action: PropTypes.string,
      failure_count: PropTypes.number,
      success_count: PropTypes.number,
      attempted_amount: PropTypes.number
    })
  ),
  bookingStatus: PropTypes.string,
  bookingUUID: PropTypes.string,
  remainingBalance: PropTypes.number,
  paymentStatus: PropTypes.string
}

export default PaymentHistory
